/******************************************************************************
 *** BgtSpcDpstBgtFrmt ** 특별회계기금 예산 편성 * 수입 예산 편성
*******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import Axios from "@/js/common/Axios";

interface Props {
    ognzCd: string;
    acctYear: string;
    callTime: Date;
    systemCode: string;
    setCallTime: Function;
}

const BgtSpcDpstBgtFrmt = ({ ognzCd, acctYear, callTime, systemCode, setCallTime }: Props) => {
    const [dataArr, setDataArr] = React.useState<any[]>([]);
    const [sumFrmtAmt, setSumFrmtAmt] = React.useState<string>(""); // 수입예산 합계 금액
    const [cfmtnYn   , setCfmtnYn   ] = React.useState<string>("N"); // 수입예산 편성 확정 여부

    React.useEffect(() => {
        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/bgt/spc/frmt/select-bgt-spc-bgt-frmt.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd: ognzCd,
                bgtYear: acctYear,
                spcAcntgSeCd: systemCode,
                bgtSbjctSeCd: "01",
                acntgYear: acctYear, // 회계연도 마감여부 판단
                bgtSeCd: systemCode,    // 수입 예산편성 확정여부
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);
                setSumFrmtAmt(res.result.listSum?.frmtAmt ?? ""); // 수입 예산편성 합계 금액
                setCfmtnYn(res.result.detail?.cfmtnYn); // 수입 예산편성 확정여부
                AppContext.hideSpinner();
            },
        });
    }, [ognzCd, acctYear, systemCode, callTime]);

    const doChangeAmount = (index: number, newValue: string) => {
        if (cfmtnYn === "Y") return;

        const newDataArr = [...dataArr];
        newDataArr[index].frmtAmt = Util.uncomma(newValue);
        setDataArr(newDataArr);

        const nextSumFrmtAmt = newDataArr.reduce((acc, row) => {
            return acc + (Number(Util.uncomma(String(row.frmtAmt))) || 0);
        }, 0);
        setSumFrmtAmt(String(nextSumFrmtAmt));
    };

    const doSave = () => {
        if (cfmtnYn === "Y") return;

        AppContext.openAlert({
            title: "수입 예산 편성 저장",
            msg: "수입 예산 편성을 저장하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "저장",
            handleConfirm: () => {
                AppContext.showSpinner();

                Axios.dataAccess({
                    url: "system/bgt/spc/frmt/save-bgt-spc-bgt-frmt.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgSeCd: systemCode,
                        dataList: dataArr,
                    },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "수입 예산 편성 저장",
                            msg: "수입 예산 편성이 저장되었습니다.",
                            icon: "info",
                        });
                        setCallTime(new Date());
                    },
                });
            },
        });
    };

    const doConfirm = () => {
        if (cfmtnYn === "Y") return;

        AppContext.openAlert({
            title: "수입 예산 편성 확정",
            msg: "수입 예산 편성을 확정하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "확정",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/frmt/save-bgt-spc-bgt-frmt.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgSeCd: systemCode,
                        dataList: dataArr,
                    },
                    onSuccessFn: () => {
                        Axios.dataAccess({
                            url: "system/sys/frmt/cfmtn/save-bgt-mbsds-frmt-cfmtn-dtl.do",
                            methodType: "post",
                            paramType: "json",
                            paramData: {
                                orgCd: ognzCd,
                                bgtYear: acctYear,
                                bgtSeCd: systemCode,
                                cfmtnYn: "Y",
                            },
                            onSuccessFn: () => {
                                AppContext.hideSpinner();

                                AppContext.openAlert({
                                    title: "수입 예산 편성 확정",
                                    msg: "수입 예산 편성이 확정되었습니다.",
                                    icon: "info",
                                });

                                setCfmtnYn("Y");
                                setCallTime(new Date());
                            },
                        });
                    },
                });
            },
        });
    };

  const isConfirmed = cfmtnYn === "Y";

    return (
        <>
            <div className="dpFlx aiC jcSb mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">수입 예산 편성</div>
                {cfmtnYn === "Y" &&
                    <span className="fcCrimson fs16 fm-c">수입 예산 편성이 확정되었습니다.</span>
                }
                <div className="dpFlx aiC jcR h44 gap10">
                    <Button buttonName="예산 저장"
                        width={120} height={40}
                        fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                        variant="styTempBtn"
                        borderRadius={6}
                        buttonClass="dpFlx aiC jcC"
                        onClickFunc={doSave}
                        disabled={cfmtnYn === "Y" ? true : false}
                    />
                    <Button buttonName="예산 확정"
                        width={120} height={40}
                        fontFamily="fm-b" fontSize={16} letterSpacing={-1}
                        variant="styCompleteBtn"
                        borderRadius={6}
                        buttonClass="dpFlx aiC jcC"
                        onClickFunc={doConfirm}
                        disabled={cfmtnYn === "Y" ? true : false}
                    />
                </div>
            </div>

            <div className="w100p bsBdb bgcFDFCFC">
                <div className="w100p"
                    style={{maxHeight: 540, overflowY: "auto", overflowX: "hidden", borderTop: "1px solid #C5C5C5", position: "relative", zIndex: 0,}}
                >
                    <table className="w100p tlF tbl-collapse">
                        <colgroup>
                            <col className="w50" />
                            <col className="w150" />
                            <col className="w180" />
                            <col className="w200" />
                            <col className="w150" />
                        </colgroup>

                        <thead>
                        <tr>
                            {["번호", "관", "항", "목", "편성 금액"].map((label) => (
                                <th key={label} className="p0 bd1 bdcEDEDED" style={{ position: "sticky", top: 0, zIndex: 5 }}>
                                    <div className="h40 dpFlx aiC jcC pw12 bgcF1F8FF fm-Pretendard-Medium fs14 fw400 fc34495e ls1 wsNw ofH toE">
                                        {label}
                                    </div>
                                </th>
                            ))}
                        </tr>
                        </thead>

                        <tbody>
                            {dataArr.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p0 bd1 bdcEDEDED">
                                        <div className="ph25 taC fc999999 fm-Pretendard-Regular fs14 ls075">조회된 데이터가 없습니다.</div>
                                    </td>
                                </tr>
                            ) : (
                                dataArr.map((row, index) => (
                                    <tr key={index} className="bgcF9F9F9-hover transitionBgc">
                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE jcC">
                                                {index + 1}
                                            </div>
                                        </td>
                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE">
                                                {row.bgtSbjct1stNm}
                                            </div>
                                        </td>
                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE">
                                                {row.bgtSbjct2stNm}
                                            </div>
                                        </td>
                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs14 fc666666 ls075 wsNw ofH toE">
                                                {row.bgtSbjct3stNm}
                                            </div>
                                        </td>
                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC jcE pw12">
                                                <NumberTextBox value={row.frmtAmt}
                                                    maxLength={12}
                                                    fontFamily="fm-c" fontSize={13} color="#121212" letterSpacing={-1}
                                                    borderColor="#BFBFBF" borderOnColor="#111"
                                                    thousandsGroupStyle="thousand" thousandSeparator=","
                                                    placeholder="0"
                                                    className={`w200 h30 ml10 bd1 bdrd2 ${isConfirmed ? "bgcF6F6F6" : ""}`}
                                                    inputClass={"taR mr5"}
                                                    onChange={(v: string) => doChangeAmount(index, v)}
                                                    disabled={isConfirmed}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>

                        <tfoot>
                            <tr className="bgcDCDCDC" style={{ position: "sticky", bottom: 0, zIndex: 4 }}>
                                <td colSpan={4} className="p0 bd1 bdcEDEDED">
                                <div className="h35 dpFlx aiC jcC pw12 fm-b fs14 ls06 fc111111 wsNw">합계</div>
                                </td>
                                <td className="p0 bd1 bdcEDEDED">
                                <span className="h35 dpFlx aiC jcE pw12 mr8">{Util.comma(sumFrmtAmt)}</span>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </>
    );
};

export default BgtSpcDpstBgtFrmt;
